import Stack from '@mui/material/Stack';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DocumentTitle from '../../utilComponents/DocumentTitle';
import Paper from '../../apiForm/panel/Paper';
import useExportApi from './useExportApi';
import { useAlert } from '../../apiForm/toast';

interface Shop {
  id: string;
  name: string;
}

export default function ThirdPartyExportView() {
  const { t } = useTranslation();
  const api = useExportApi();
  const alert = useAlert();
  const [shop, setShop] = useState<Shop | null>(null);
  const [shops, setShops] = useState<Shop[]>([]);
  const [loadingShops, setLoadingShops] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;

    (async () => {
      setLoadingShops(true);

      try {
        const data = await api.getShops(null);

        if (active) {
          setShops(data || []);
        }
      } catch (e) {
        alert.error({ message: t('export.thirdParty.genericError') });
      } finally {
        if (active) {
          setLoadingShops(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [api, t, alert]);

  const handleTrigger = async () => {
    if (!shop?.id) {
      alert.error({ message: t('export.thirdParty.missingShopId') });
      return;
    }

    setLoading(true);

    try {
      await api.triggerThirdPartyExport(shop.id);

      alert.success({ message: t('export.thirdParty.successMessage') });
      setShop(null);
    } catch (e) {
      alert.error({ message: t('export.thirdParty.genericError') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DocumentTitle translationID="export.thirdParty.headline" />
      <Paper>
        <Stack direction="row" alignItems="center" gap={1} mb={2}>
          <Typography variant="h5">
            {t('export.thirdParty.headline')}
          </Typography>
        </Stack>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Autocomplete
            options={shops}
            getOptionLabel={(option) => `${option.name} (${option.id})`}
            value={shop}
            onChange={(_, newValue) => setShop(newValue)}
            disabled={loadingShops || loading}
            loading={loadingShops}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('export.thirdParty.shopIdLabel')}
                size="small"
                sx={{ minWidth: 300 }}
              />
            )}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlayArrowIcon />}
            onClick={handleTrigger}
            disabled={loading || !shop}
          >
            {t('export.thirdParty.triggerButton')}
          </Button>
        </Box>
      </Paper>
    </>
  );
}
